import { homeStats } from '@/api/home'
import {
  projectList,
  buildingList,
  floorList,
  deviceList,
  baseStationList
} from '@/api'
import resource from '@/mock/store'
import {
  assembleProjectTree,
  apiProjectToUi,
  apiAdminToUi,
  apiDeviceToBeacon,
  apiBaseStationToUi,
  buildingsToZones
} from '@/api/adapters'
import { loadAdminList } from './adminService'
import { loadJobTypes } from './jobTypeService'
import { loadAlarms } from './alarmService'

const PAGE = { page: 1, page_size: 100 }
const BASE_STATION_PAGE = { page: 1, page_size: 999 }

export async function fetchBaseStationList(params = {}) {
  const res = await baseStationList({ ...BASE_STATION_PAGE, ...params })
  return (res.data && res.data.list) || []
}

export async function fetchAllPages(fetcher, params = {}) {
  const first = await fetcher({ ...PAGE, ...params })
  const data = first.data || {}
  let list = data.list || []
  const total = data.total || list.length
  if (total > list.length) {
    const pages = Math.ceil(total / PAGE.page_size)
    const tasks = []
    for (let page = 2; page <= pages; page++) {
      tasks.push(fetcher({ ...params, page, page_size: PAGE.page_size }))
    }
    const rest = await Promise.all(tasks)
    rest.forEach(r => {
      list = list.concat((r.data && r.data.list) || [])
    })
  }
  return list
}

let projectListParams = {}

export function setProjectListParams(params = {}) {
  projectListParams = { ...params }
}

export function getProjectListParams() {
  return { ...projectListParams }
}

function buildProjectListQuery(params = {}) {
  const query = {}
  const keyword = params.keyword != null ? String(params.keyword).trim() : ''
  if (keyword) query.keyword = keyword
  if (params.type) query.type = params.type
  return query
}

function deviceContext() {
  return {
    projects: resource.projects,
    buildings: resource.apiBuildings,
    floors: resource.apiFloors.map(f => ({ id: f.id, name: f.name })),
    jobTypes: resource.jobTypes
  }
}

function applyProjectTree(projects, buildings, floors) {
  const uiProjects = assembleProjectTree(projects, buildings, floors)
  resource.projects.splice(0, resource.projects.length, ...uiProjects)
  resource.apiProjects = projects
  resource.apiBuildings = buildings
  resource.apiFloors = floors
  resource.zones = buildingsToZones(buildings, projects)
  return uiProjects
}

function applyFlatProjects(projects) {
  const uiProjects = projects.map(p => apiProjectToUi(p))
  resource.projects.splice(0, resource.projects.length, ...uiProjects)
  resource.apiProjects = projects
  return uiProjects
}

/** 首页 KPI；传 project_id 时部分指标按项目过滤 */
export async function loadHomeStats(projectId) {
  const pid = Number(projectId)
  const payload = pid > 0 ? { project_id: pid } : {}
  const statsRes = await homeStats(payload).catch(() => ({ data: {} }))
  const stats = statsRes.data || {}
  Object.assign(resource.homeStats, {
    project_total: stats.project_total || 0,
    device_total: stats.device_total || 0,
    device_online_count: stats.device_online_count || 0,
    base_station_total: stats.base_station_total || 0,
    base_station_online_count: stats.base_station_online_count || 0
  })
  return resource.homeStats
}

/** 看板首页：仅 KPI + 项目列表 */
async function loadDashboard() {
  const [, projects] = await Promise.all([
    loadHomeStats(),
    fetchAllPages(projectList)
  ])
  applyFlatProjects(projects)
  resource.apiBuildings = []
  resource.apiFloors = []
  resource.zones = []
  resource.beacons.splice(0, resource.beacons.length)
  resource.stations.splice(0, resource.stations.length)
}

/** 工区页进入：仅项目列表 */
async function loadZone() {
  const listQuery = buildProjectListQuery(projectListParams)
  const projects = await fetchAllPages(projectList, listQuery)
  applyFlatProjects(projects)
  resource.apiBuildings = []
  resource.apiFloors = []
  resource.stations.splice(0, resource.stations.length)
}

/** 工区：选中项目后加载 Building + Floor（不拉基站列表） */
export async function loadZoneProjectDetail(projectId) {
  if (!projectId) return null
  const pid = Number(projectId)
  const apiProject = resource.apiProjects.find(p => Number(p.id) === pid)
  if (!apiProject) return null

  const [buildings, floors] = await Promise.all([
    fetchAllPages(buildingList, { project_id: pid }),
    fetchAllPages(floorList, { project_id: pid })
  ])

  const uiProject = assembleProjectTree([apiProject], buildings, floors)[0]
  const idx = resource.projects.findIndex(p => Number(p.id) === pid)
  if (idx >= 0) resource.projects.splice(idx, 1, uiProject)

  resource.apiBuildings = [
    ...resource.apiBuildings.filter(b => Number(b.project_id) !== pid),
    ...buildings
  ]
  resource.apiFloors = [
    ...resource.apiFloors.filter(f => Number(f.project_id) !== pid),
    ...floors
  ]

  resource.stations.splice(0, resource.stations.length)

  return uiProject
}

/** 看板：按项目/楼层拉在线设备 */
export async function loadDashboardBeacons(params = {}) {
  const query = { online_status: 1 }
  if (params.floorId) {
    query.floor_id = Number(params.floorId)
  } else if (params.projectId) {
    query.project_id = Number(params.projectId)
  } else {
    resource.beacons.splice(0, resource.beacons.length)
    return []
  }
  const devices = await fetchAllPages(deviceList, query)
  const list = devices.map(d => apiDeviceToBeacon(d, deviceContext()))
  resource.beacons.splice(0, resource.beacons.length, ...list)
  return list
}

/** 看板/工区：按项目加载安全帽（设备，含离线） */
export async function loadProjectBeacons(projectId) {
  const pid = Number(projectId)
  if (!pid) {
    resource.beacons.splice(0, resource.beacons.length)
    return []
  }
  const devices = await fetchAllPages(deviceList, { project_id: pid })
  const list = devices.map(d => apiDeviceToBeacon(d, deviceContext()))
  resource.beacons.splice(0, resource.beacons.length, ...list)
  return list
}

/** 工区图纸工作台：按项目加载基站 */
export async function loadProjectStations(projectId) {
  const pid = Number(projectId)
  if (!pid) {
    resource.stations.splice(0, resource.stations.length)
    return []
  }
  const stations = await fetchBaseStationList({ project_id: pid })
  const list = stations.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  resource.stations.splice(0, resource.stations.length, ...list)
  return list
}

/** 工区：拉取未绑定基站（BaseStation/List only_unbound=1） */
export async function loadUnboundStations() {
  const list = await fetchBaseStationList({ only_unbound: 1 })
  return list.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
}

/** 工区项目搜索：仅 Project/List */
export async function searchProjectList(params = {}) {
  projectListParams = { ...params }
  const listQuery = buildProjectListQuery(projectListParams)
  const projects = await fetchAllPages(projectList, listQuery)
  applyFlatProjects(projects)
  resource.apiBuildings = []
  resource.apiFloors = []
  resource.stations.splice(0, resource.stations.length)
  return resource.projects
}

/** 工区 CRUD 后：刷新项目列表 */
export async function refreshProjectList() {
  return loadZone()
}

/** 工区 CRUD 后：刷新单个项目的工区树 */
export async function refreshZoneProjectDetail(projectId) {
  return loadZoneProjectDetail(projectId)
}

/** 设备页：仅安全帽列表（含工种字典） */
export async function loadDeviceBeacons() {
  await loadJobTypes().catch(() => {})
  const devices = await fetchAllPages(deviceList)
  const ctx = deviceContext()
  resource.beacons.splice(
    0,
    resource.beacons.length,
    ...devices.map(d => apiDeviceToBeacon(d, ctx))
  )
  return resource.beacons
}

/** 设备页：仅基站列表 */
export async function loadDeviceStations() {
  const stations = await fetchBaseStationList()
  resource.stations.splice(
    0,
    resource.stations.length,
    ...stations.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  )
  return resource.stations
}

let workStatsZoneTreeLoaded = false

async function loadWorkStats() {
  const listQuery = buildProjectListQuery(projectListParams)
  const projects = await fetchAllPages(projectList, listQuery)
  applyFlatProjects(projects)
}

/** 作业统计：工区树（按需加载） */
export async function ensureWorkStatsZoneTree() {
  if (workStatsZoneTreeLoaded && resource.apiBuildings.length) return resource.projects
  const [projects, buildings, floors] = await Promise.all([
    fetchAllPages(projectList),
    fetchAllPages(buildingList),
    fetchAllPages(floorList)
  ])
  applyProjectTree(projects, buildings, floors)
  workStatsZoneTreeLoaded = true
  return resource.projects
}

/** 作业统计：安全帽列表（按需加载） */
export async function ensureWorkStatsBeacons() {
  if (resource.beacons.length) return resource.beacons
  await loadJobTypes().catch(() => {})
  const devices = await fetchAllPages(deviceList)
  const ctx = deviceContext()
  resource.beacons.splice(
    0,
    resource.beacons.length,
    ...devices.map(d => apiDeviceToBeacon(d, ctx))
  )
  return resource.beacons
}

let deviceFormOptionsLoaded = false

/** 设备页：表单/绑定弹窗所需 Project + Building + Floor */
export async function loadDeviceFormOptions() {
  if (deviceFormOptionsLoaded) return resource.projects
  const [projects, buildings, floors] = await Promise.all([
    fetchAllPages(projectList),
    fetchAllPages(buildingList),
    fetchAllPages(floorList)
  ])
  applyProjectTree(projects, buildings, floors)
  deviceFormOptionsLoaded = true
  return resource.projects
}

export function resetDevicePageCache() {
  deviceFormOptionsLoaded = false
}

/** @deprecated 使用 loadDeviceBeacons / loadDeviceStations */
async function loadDevice() {
  return loadDeviceBeacons()
}

/** 轨迹：项目树 + 设备列表 */
async function loadTrace() {
  const [projects, buildings, floors, devices] = await Promise.all([
    fetchAllPages(projectList),
    fetchAllPages(buildingList),
    fetchAllPages(floorList),
    fetchAllPages(deviceList)
  ])
  await loadJobTypes().catch(() => {})
  applyProjectTree(projects, buildings, floors)
  const ctx = deviceContext()
  resource.beacons.splice(
    0,
    resource.beacons.length,
    ...devices.map(d => apiDeviceToBeacon(d, ctx))
  )
}

/** CRUD 后：刷新项目树（Project + Building + Floor） */
export async function refreshProjectTree(params, options = {}) {
  const persist = options.persistParams !== false
  const q = params !== undefined ? params : projectListParams
  const listQuery = buildProjectListQuery(q)
  const [projects, buildings, floors] = await Promise.all([
    fetchAllPages(projectList, listQuery),
    fetchAllPages(buildingList),
    fetchAllPages(floorList)
  ])
  if (params !== undefined && persist) projectListParams = { ...q }
  return applyProjectTree(projects, buildings, floors)
}

/** CRUD 后：仅刷新信标 */
export async function refreshDevices() {
  await loadJobTypes().catch(() => {})
  const devices = await fetchAllPages(deviceList)
  const ctx = deviceContext()
  resource.beacons.splice(
    0,
    resource.beacons.length,
    ...devices.map(d => apiDeviceToBeacon(d, ctx))
  )
  return resource.beacons
}

/** CRUD 后：仅刷新基站 */
export async function refreshStations() {
  const stations = await fetchBaseStationList()
  resource.stations.splice(
    0,
    resource.stations.length,
    ...stations.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  )
  return resource.stations
}

/** @deprecated 仅兼容旧调用，内部不再使用 */
export function syncProjects(params, options) {
  return refreshProjectTree(params, options)
}

export function syncDevices() {
  return refreshDevices()
}

export function syncStations() {
  return refreshStations()
}

export function syncResource() {
  return Promise.all([refreshProjectTree(), refreshDevices(), refreshStations()])
}

/** 路由进入时：各页只拉自己用到的接口 */
export function loadPageData(routeName, user) {
  switch (routeName) {
    case 'Dashboard':
      return loadDashboard()
    case 'ZoneManage':
      return loadZone()
    case 'DeviceManage':
      resetDevicePageCache()
      resource.stations.splice(0, resource.stations.length)
      return loadDeviceBeacons()
    case 'AlarmManage':
      return loadAlarms()
    case 'JobTypeManage':
      return loadJobTypes()
    case 'TracePlayback':
      return loadTrace()
    case 'WorkStats':
      return loadWorkStats()
    case 'SystemSettings':
      return loadAdminList(user)
    default:
      return Promise.resolve()
  }
}

export function setAccounts(list) {
  resource.accounts.splice(0, resource.accounts.length, ...list.map(apiAdminToUi))
}

export function upsertAccount(admin) {
  const ui = apiAdminToUi(admin)
  const idx = resource.accounts.findIndex(a => a.id === ui.id)
  if (idx > -1) resource.accounts.splice(idx, 1, ui)
  else resource.accounts.push(ui)
}

export function removeAccounts(ids) {
  const idSet = new Set(ids)
  for (let i = resource.accounts.length - 1; i >= 0; i--) {
    if (idSet.has(resource.accounts[i].id)) resource.accounts.splice(i, 1)
  }
}

export default {
  loadPageData,
  loadHomeStats,
  loadDeviceBeacons,
  loadDeviceStations,
  loadDeviceFormOptions,
  resetDevicePageCache,
  searchProjectList,
  loadZoneProjectDetail,
  refreshProjectList,
  refreshZoneProjectDetail,
  refreshProjectTree,
  refreshDevices,
  refreshStations,
  syncProjects,
  syncDevices,
  syncStations,
  syncResource,
  setProjectListParams,
  getProjectListParams,
  fetchAllPages,
  fetchBaseStationList,
  ensureWorkStatsZoneTree,
  ensureWorkStatsBeacons,
  setAccounts,
  upsertAccount,
  removeAccounts
}
